import dayjs from "dayjs";

import { serverResponseFormat } from "@similarweb/pro-logic-components";

import { useApiQuery } from "app/modules/api-client";
import type { Data } from "app/modules/api-client/queryTypes";
import { BasicDurations } from "app/services/DurationService";
import { chosenSites } from "app/common/services/chosenSites";
import { EDeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import type { EDataType } from "../../../advertisers/overview/components/types";
import type { DisplayAdvertiserPublisherParams } from "../../hooks/useDisplayAdvertiserPublisherDataAndParams";
import type { AdIntelligenceController } from "../../shared/types/types";
import type { GranularityKey, DevicePerformanceData } from "../types";

export interface ISeriesData {
    id: string;
    name: string;
    data: Array<{ x: number; y: number; change?: number }>;
    color: string;
    visible: boolean;
    domain: string;
    total?: number;
}

export const usePerformanceOverTimeCompareData = (
    params: DisplayAdvertiserPublisherParams,
    from: string,
    to: string,
    isWindow: boolean,
    controller: AdIntelligenceController = "advertiser",
    currentGranularityKey: GranularityKey,
    selectedMetric: EDataType,
) => {
    const methodName =
        controller === "advertiser"
            ? "adIntelligenceAdvertiserGraphV2UnifiedList"
            : "adIntelligencePublisherGraphV2UnifiedList";

    const keyArray = params.key.split(",");

    type ApiResponse = Data<"AdIntelligenceMediaPartners", typeof methodName>;

    const selectTransformedData = (response: ApiResponse) => {
        const { data, hasAdIntelMetrics } = response || {};
        if (!data) {
            return { graphData: [], hasAdIntelMetrics };
        }
        const graphData: ISeriesData[] = keyArray.map((domain) => {
            const deviceKey = EDeviceType.TOTAL.toLowerCase();
            const domainData = data[domain];

            const sitePerformanceData = domainData?.[deviceKey] as DevicePerformanceData;
            const breakdownByGranularity = sitePerformanceData?.[currentGranularityKey];
            const totalMetrics = sitePerformanceData?.total;
            const totalValue = totalMetrics?.[selectedMetric];

            if (!breakdownByGranularity) {
                return {
                    id: domain,
                    name: domain,
                    domain,
                    data: [],
                    color: chosenSites.getSiteColor(domain),
                    total: totalValue,
                    visible: true,
                };
            }

            const seriesData = Object.entries(breakdownByGranularity).map(([dateString, performanceItem]) => {
                const timestampValue = dayjs.utc(dateString, serverResponseFormat).valueOf();
                if (!performanceItem) {
                    return { x: timestampValue, y: 0, change: 0 };
                }
                const metricData = performanceItem[selectedMetric];
                const value = metricData?.value || 0;
                const change = metricData?.change || 0;
                return { x: timestampValue, y: value, change };
            });

            return {
                id: domain,
                name: domain,
                domain,
                data: seriesData,
                color: chosenSites.getSiteColor(domain),
                total: totalValue,
                visible: true,
            };
        });

        return { graphData, hasAdIntelMetrics };
    };

    type ISelectedData = ReturnType<typeof selectTransformedData>;

    return useApiQuery<"AdIntelligenceMediaPartners", typeof methodName, ISelectedData>(
        {
            tag: "AdIntelligenceMediaPartners",
            methodName,
            options: {
                select: selectTransformedData,
            },
        },
        {
            country: params.country,
            key: keyArray,
            from,
            to,
            isWindow,
            ...(isWindow ? { latest: BasicDurations.LAST_TWENTY_EIGHT_DAYS } : {}),
        },
    );
};
